import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSearch, FiPlusCircle, FiEdit2, FiTrash2, FiBookOpen, FiRepeat } from "react-icons/fi";
import libraryService from "../../services/libraryService";

const PAGE_SIZE = 20;

export default function BookList() {
    const navigate = useNavigate();
    const [books, setBooks] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchBooks = async (searchValue = search, pageValue = page) => {
        setLoading(true);
        try {
            const res = await libraryService.getBooks({ search: searchValue, page: pageValue, limit: PAGE_SIZE });
            setBooks(res.data.books || []);
            setPages(res.data.pages || 1);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBooks(search, page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            fetchBooks(search, 1);
        }, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await libraryService.removeBook(deleteTarget._id);
            toast.success("Book deleted.");
            setDeleteTarget(null);
            fetchBooks(search, page);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Library — Books</h1>
                    <p className="page-subtitle">Browse the catalog, manage copies, and issue books to students.</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/library/issues")}>
                        <FiRepeat size={14} style={{ marginRight: 6 }} />
                        Issue / Return
                    </button>
                    <button className="btn btn-primary" onClick={() => navigate("/library/books/add")}>
                        <FiPlusCircle size={14} style={{ marginRight: 6 }} />
                        Add Book
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div className="search-bar">
                        <FiSearch size={14} color="var(--text-muted)" />
                        <input
                            placeholder="Search by title, author, or ISBN..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="loading-center">
                            <div className="spinner" />
                            Loading books...
                        </div>
                    ) : books.length === 0 ? (
                        <div className="empty-state">
                            <FiBookOpen />
                            <p>No books found. Try adjusting your search or add a new book.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Author</th>
                                        <th>Category</th>
                                        <th>Copies</th>
                                        <th>Shelf</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {books.map((book) => (
                                        <tr key={book._id}>
                                            <td>
                                                <Link to={`/library/books/${book._id}`} style={{ fontWeight: 600, color: "var(--primary)" }}>
                                                    {book.title}
                                                </Link>
                                                {book.isbn && (
                                                    <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>ISBN: {book.isbn}</div>
                                                )}
                                            </td>
                                            <td>{book.author}</td>
                                            <td>{book.category || "—"}</td>
                                            <td>
                                                <span className={`badge ${book.availableCopies > 0 ? "badge-green" : "badge-blue"}`}>
                                                    {book.availableCopies} / {book.totalCopies} available
                                                </span>
                                            </td>
                                            <td>{book.shelfLocation || "—"}</td>
                                            <td>
                                                <div style={{ display: "flex", gap: "0.4rem", justifyContent: "flex-end" }}>
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        title="Edit"
                                                        onClick={() => navigate(`/library/books/${book._id}/edit`)}
                                                    >
                                                        <FiEdit2 size={13} />
                                                    </button>
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        title="Delete"
                                                        onClick={() => setDeleteTarget(book)}
                                                    >
                                                        <FiTrash2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {pages > 1 && (
                    <div className="pagination">
                        {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                            <button key={p} className={p === page ? "active" : ""} onClick={() => setPage(p)}>
                                {p}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {deleteTarget && (
                <div className="modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Delete Book</h3>
                        <p>
                            Are you sure you want to delete <strong>{deleteTarget.title}</strong>? This can't be undone, and will
                            fail if any copies are currently issued.
                        </p>
                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                                Cancel
                            </button>
                            <button className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
                                {deleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}